import React, { type Ref, forwardRef } from "react";
import { UncontrolledTooltip } from "reactstrap";
import styled from "styled-components";
import { Formatter } from "./Formatter";
import { CopyButton } from "src/shared/components/CopyButton";
import { useTooltip } from "src/shared/components/useTooltip";

export interface ILinkFormatterProps {
  children: string;
  className?: string;
}

const LinkFormatterInner = forwardRef(
  (
    { children, className }: ILinkFormatterProps,
    ref: Ref<HTMLAnchorElement>,
  ) => (
    <a
      href={children}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      ref={ref}
    >
      <Formatter>{children}</Formatter>
    </a>
  ),
);
LinkFormatterInner.displayName = "LinkFormatterInner";

const StyledLinkFormatterInner = styled(LinkFormatterInner)<{
  showEllipsis?: boolean;
  maxWidth?: string;
}>(({ showEllipsis, maxWidth }) => ({
  flexGrow: 1,
  ...(!showEllipsis && {
    wordBreak: "break-all",
  }),
  ...(showEllipsis && {
    maxWidth,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
}));

export interface IFeaturedLinkFormatterProps extends ILinkFormatterProps {
  inline?: boolean;
  showEllipsis?: boolean;
  maxWidth?: string;
  isCopyable?: boolean;
  showTooltip?: boolean;
}

const LinkFormatterStyled = styled.span<{ inline?: boolean }>(
  ({ inline = true }) => ({
    display: inline ? "inline-flex" : "flex",
    maxWidth: "100%",
  }),
);

export const LinkFormatter = ({
  inline,
  isCopyable = false,
  children,
  showEllipsis,
  maxWidth,
  showTooltip,
  ...rest
}: IFeaturedLinkFormatterProps) => {
  const { containerRef, target } = useTooltip();
  return (
    <LinkFormatterStyled inline={inline}>
      <StyledLinkFormatterInner
        showEllipsis={showEllipsis}
        maxWidth={maxWidth}
        {...rest}
        ref={containerRef}
      >
        {children}
      </StyledLinkFormatterInner>
      {showTooltip && target && (
        <UncontrolledTooltip placement="top" target={target}>
          {children}
        </UncontrolledTooltip>
      )}
      {isCopyable && <CopyButton value={children} />}
    </LinkFormatterStyled>
  );
};

/**
 * @deprecated Use LinkFormatter
 */
export const FeaturedLinkFormatter = LinkFormatter;
